<template>
  <div class="fts-form-script-wrapper">
    <fts-tabs class="fts-form-script">
      <div class="script-header" slot="title" slot-scope="{title}">
        <div class="script-header__indicator" v-if="scriptTabIndicator(title)"></div>
        <div class="script-header__label">{{title}}</div>
      </div>
      <fts-tab title="Backend Script">
        <fts-script-editor
          editor-id="backendScript"
          :save-button="saveButton"
          v-model="formData.backendScript"
          lang="python"
          label="Backend Script"
          class="fts-form-group"
          @save="$emit('save')"
          @fullview-change="onFullViewChange('backendScript', $event)"
        />
        <!-- Mechanism B2 (formsync-declared-triggers-proposal.md §3): declared sync trigger
             fields. Declared => the form round-trips only when one of these fields changes;
             empty => today's behavior, every edit syncs while a backend script exists. -->
        <div class="fts-form-group script-triggers" v-if="formData.backendScript">
          <label class="script-triggers__label">Sync trigger fields</label>
          <div class="script-triggers__hint">
            This script re-runs only when one of these fields changes; other edits stay local
            until then. Leave empty to keep syncing on every edit. Pick <b>None</b> to never
            re-run on field edits (initial run only), or <b>All events</b> to re-run whenever
            a form event is pushed (e.g. by a button).
          </div>
          <!-- SS05974: commit from updateValue, never from input. The vendored select's ajax
               mixin emits input:null on every search keystroke (single-select free-text
               convention), so listening to input wiped every declared trigger as soon as
               the user typed to find the next field. updateValue fires only when the
               select replaces its whole value (clear), alongside that same input. -->
          <v-select
            multiple
            append-to-body
            label="label"
            :options="scriptTriggerOptions"
            :value="selectedScriptTriggers"
            :reduce="reduceScriptTrigger"
            comfort-design
            @updateValue="setScriptTriggers"
            @option:selected="addScriptTrigger"
            @option:deselected="removeScriptTrigger"
            @option:cleared="setScriptTriggers([])"
          >
            <!-- the stored value is the raw {{form.*}} token (D-5), which is what the select
                 would otherwise print. Resolve it back to the field's display name — via this
                 design's elements, else the module information (a declaration can outlive the
                 field's placement on this activity). -->
            <template #selected-option="{ label: token }">
              <span
                class="script-triggers__selected"
                :class="{ 'script-triggers__selected--foreign': !isDesignTriggerToken(token) }"
                :title="scriptTriggerTitle(token)"
              >{{ scriptTriggerLabel(token) }}</span>
            </template>
          </v-select>
          <!-- D-8: the effective combined set from the SyncTriggers classification -->
          <div class="script-triggers__combined" v-if="combinedSyncFields.length">
            Form syncs on:
            <span
              class="script-triggers__chip"
              v-for="item in combinedSyncFields"
              :key="item.label"
              :title="item.reasons"
            >{{ item.label }}</span>
          </div>
        </div>
        <div class="fts-form-script__debug" v-if="canDebug">
          <fts-debug-panel
            ref="debugPanel"
            :state-key="activeFullViewEditor || 'backendScript'"
            :inline="!anyFullView"
            :floating="anyFullView"
          />
        </div>
        <fts-script-editor
          editor-id="payloadScript"
          v-model="formData.payload"
          lang="python"
          label="Payload"
          disabled-dictionary
          @fullview-change="onFullViewChange('payloadScript', $event)"
        />
      </fts-tab>
      <fts-tab title="Before Done Script">
        <fts-script-editor
          editor-id="beforeDoneScript"
          :save-button="saveButton"
          v-model="formData.beforeDoneScript"
          lang="javascript"
          disabled-dictionary
          @save="$emit('save')"
          @fullview-change="onFullViewChange('beforeDoneScript', $event)"
        />
      </fts-tab>
      <fts-tab title="After Done Script">
        <fts-script-editor
          editor-id="afterDoneScript"
          :save-button="saveButton"
          v-model="formData.afterDoneScript"
          lang="javascript"
          disabled-dictionary
          @save="$emit('save')"
          @fullview-change="onFullViewChange('afterDoneScript', $event)"
        />
      </fts-tab>
    </fts-tabs>
  </div>
</template>

<script>
import Vue from 'vue';
import { authorizationStore } from '../store/authorizationStore';
import { automationDesignStore } from '../store/automationDesignStore';
import commons from '../commons/commons';
import vSelect from '../vue-select';

import FTSBtn from '../elements/FTSBtn';
import FTSTabs from '../elements/tabs/FTSTabs';
import FTSTab from '../elements/tabs/FTSTab';
import FTSScriptEditor from './FTSScriptEditor';
import FTSDebugPanel from '../elements/FTSDebugPanel';

export default {
  components: {
    "fts-btn": FTSBtn,
    "fts-tabs": FTSTabs,
    "fts-tab": FTSTab,
    "fts-script-editor": FTSScriptEditor,
    "fts-debug-panel": FTSDebugPanel,
    "v-select": vSelect
  },
  props: {
    formData: Object,
    saveButton: Boolean,
    /* module-wide field definitions (canvasInformation) — resolves display names for declared
       trigger tokens whose field is not on this activity's design */
    information: Array
  },
  data: () => ({
    fullViewMap: {}
  }),
  computed: {
    canDebug() {
      return authorizationStore.getters.isInternal || authorizationStore.getters.isBeta;
    },
    anyFullView() {
      return Object.values(this.fullViewMap).some(Boolean);
    },
    activeFullViewEditor() {
      const pair = Object.entries(this.fullViewMap).find(([, value]) => value);
      return pair && pair[0];
    },
    /* Mechanism B2: declarable fields = the design's own elements (plus table columns), each
       stored as the {{form.<fieldName>}} token the backend resolves (D-5). Two reserved
       tokens ride the same envelope so backendScriptTriggers stays one uniform list:
       {{none}}  — no form trigger at all; the script runs on the initial load only.
       {{event}} — re-run on every pushed form event (designer buttons), not on field edits. */
    scriptTriggerOptions() {
      const elements = commons.getAllElements(null, this.formData) || [];
      const options = [
        { label: "None (initial only)", token: "{{none}}" },
        { label: "All events", token: "{{event}}" }
      ];
      elements.forEach(element => {
        if (!element || !element.fieldName) return;
        options.push({
          label: this.triggerFieldLabel(element),
          token: `{{form.${element.fieldName}}}`
        });
        (element.fields || []).forEach(column => {
          if (!column || !column.fieldName) return;
          options.push({
            label: `${this.triggerFieldLabel(element)} › ${this.triggerFieldLabel(column)}`,
            token: `{{form.${element.fieldName}.${column.fieldName}}}`
          });
        });
      });
      return options;
    },
    selectedScriptTriggers() {
      return Array.isArray(this.formData.backendScriptTriggers)
        ? this.formData.backendScriptTriggers
        : [];
    },
    /* D-8: the effective combined trigger set from the SyncTriggers classification the form
       designer publishes — declaring [A] does not stop B from syncing when B feeds a formula.
       One chip per field: the verdict may list the same field once per origin, so merge rows
       sharing a label (also keeps :key unique). */
    combinedSyncFields() {
      const verdict = automationDesignStore.state.syncVerdict;
      if (!verdict || !verdict.fields || (verdict.always && verdict.always.length)) return [];
      const byLabel = new Map();
      verdict.fields.forEach(field => {
        const label = field.secondary ? `${field.primary} › ${field.secondary}` : field.primary;
        const entry = byLabel.get(label) || { label, reasons: [] };
        entry.reasons.push(...(field.targets || []).map(t => t.origin || t.primary));
        byLabel.set(label, entry);
      });
      return Array.from(byLabel.values()).map(entry => ({
        label: entry.label,
        reasons: entry.reasons
          .filter((v, i, arr) => v && arr.indexOf(v) === i)
          .join(", ")
      }));
    }
  },
  methods: {
    scriptTabIndicator(item) {
      if (item === 'Backend Script')
        return Boolean(this.formData.backendScript);
      else if (item === 'Before Done Script')
        return Boolean(this.formData.beforeDoneScript);
      else if (item === 'After Done Script')
        return Boolean(this.formData.afterDoneScript);
    },
    onFullViewChange(key, value) {
      this.$set(this.fullViewMap, key, value);
    },
    setScriptTriggers(tokens) {
      Vue.set(this.formData, 'backendScriptTriggers', (tokens || []).filter(Boolean));
    },
    /* The select pushes reduce(option) onto the bound array in place, and the bound array
       already holds tokens, so the reducer is kept idempotent: reduce(reduce(x)) === reduce(x)
       and a token handed back to it (e.g. by removeScriptTrigger) survives unchanged. */
    reduceScriptTrigger(option) {
      if (option && typeof option === "object") return option.token;
      return option;
    },
    /* SS05960: in multiple mode the select mutates its bound array in place and only emits
       `input` when it replaces the whole value (clear-all). Until backendScriptTriggers exists
       on the design, `selectedScriptTriggers` hands it a throwaway [], so the first pick was
       pushed onto a detached array and never reached the model — the field looked unselectable
       until some other edit forced the property into existence. Commit from the select/deselect
       events instead, which fire either way. Rebuilt from the committed list and deduped, so it
       stays correct once the property IS reactive and the in-place push already landed. */
    addScriptTrigger(selected) {
      /* The vendored select emits option:selected with the single option picked (npm
         vue-select 3.x emits the whole new selection instead). Accept either shape: an array
         means its last entry is the pick. The {{none}} exclusivity below lives only here. */
      const picked = Array.isArray(selected) ? selected[selected.length - 1] : selected;
      const token = this.reduceScriptTrigger(picked);
      if (!token) return;
      /* {{none}} means "no form trigger" — it cannot coexist with anything: picking it
         replaces the whole declaration, picking anything else evicts it. {{event}} and
         field tokens can combine (events fire the script, those fields also sync it). */
      if (token === "{{none}}") {
        this.setScriptTriggers([token]);
        return;
      }
      const tokens = this.selectedScriptTriggers.filter(x => x !== token && x !== "{{none}}");
      tokens.push(token);
      this.setScriptTriggers(tokens);
    },
    removeScriptTrigger(option) {
      /* SS05974: option:deselected emits the raw OPTION, while the committed list holds
         tokens - comparing the two matched nothing, so this re-committed the list
         unchanged. Harmless today because vue-select already emitted the filtered value
         through @input first, but it made the handler a no-op that looked load-bearing. */
      const token = this.reduceScriptTrigger(option);
      this.setScriptTriggers(this.selectedScriptTriggers.filter(x => x !== token));
    },
    isDesignTriggerToken(token) {
      return this.scriptTriggerOptions.some(option => option.token === token);
    },
    /* SS05989: a control's caption is its Label first, then its Name — a button carries only a
       Label (and is absent from #information), so reading displayName alone printed the raw
       fieldName. Same precedence as FTSFormElement.labelDisplayName. `label` doubles as a
       hide-the-label flag elsewhere, so only a non-empty string counts. */
    triggerFieldLabel(def) {
      if (!def) return "";
      if (typeof def.label === "string" && def.label) return def.label;
      return def.displayName || def.fieldName || "";
    },
    scriptTriggerLabel(token) {
      const option = this.scriptTriggerOptions.find(x => x.token === token);
      if (option) return option.label;
      /* not on this activity's design — resolve through the module information; a deleted
         field at least loses the {{form.*}} wrapper */
      const path = String(token).replace(/^\{\{\s*form\./, "").replace(/\s*\}\}$/, "");
      const [fieldName, columnName] = path.split(".");
      const info = (this.information || []).find(x => x && x.fieldName === fieldName);
      if (!info) return path;
      const primary = this.triggerFieldLabel(info);
      if (!columnName) return primary;
      const column = (info.fields || []).find(x => x && x.fieldName === columnName);
      return `${primary} › ${column ? this.triggerFieldLabel(column) : columnName}`;
    },
    scriptTriggerTitle(token) {
      if (this.isDesignTriggerToken(token)) return null;
      return `${token} — not a field on this activity's design`;
    }
  },
}
</script>

<style lang="scss" scoped>
.fts-form-script {
  .script-header {
    display: flex;
    align-items: center;
    white-space: nowrap;
    .script-header__indicator {
      height: .375rem;
      width: .375rem;
      margin-right: .575rem;
      border-radius: 10em;
      background-color: var(--fts-success);
    }
  }
}

.fts-form-script__debug {
  margin: .75rem 0;
}

.script-triggers {
  margin-top: .75rem;
  .script-triggers__label {
    display: block;
    margin-bottom: .25rem;
    font-weight: 600;
  }
  .script-triggers__hint {
    margin-bottom: .5rem;
    font-size: .75rem;
    opacity: .75;
  }
  /* declared trigger whose field is not on this activity's design — the title carries the
     raw token and the explanation */
  .script-triggers__selected--foreign {
    border-bottom: 1px dashed currentColor;
    opacity: .85;
  }
  .script-triggers__combined {
    margin-top: .5rem;
    font-size: .75rem;
    opacity: .85;
    .script-triggers__chip {
      display: inline-block;
      margin: .125rem .175rem 0 0;
      padding: 0 .375rem;
      border-radius: .25rem;
      background-color: rgba(var(--fts-primary-rgb), .12);
    }
  }
}
</style>
